/**
 * Instances Data Hook
 *
 * Combined hook that:
 * 1. Syncs NavTree data to instancesStore (lazy, view-aware)
 * 2. Reads from store and returns page-organized data for rendering
 *
 * ## Page-Organized Output
 *
 * All outputs are organized by page, then by storyboard:
 * - Grouped view: `currentPageStoryboardGroups` and `otherPagesGroups`
 * - Drilled view: `drilledInstances` (all instances in selected storyboard)
 *
 * ## View-Aware Sync
 *
 * - **Grouped view**: Syncs first instance per storyboard
 * - **Drilled view**: Syncs ALL instances for selected storyboard
 *
 * ## Early Exit Optimization
 *
 * Checks if store already has needed data before extracting, making re-opening
 * a previously viewed storyboard instant.
 */

import { useEffect, useMemo } from "react";
import { useCanvasStore } from "@/components/Canvas/stores/canvasStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { useCanvasId } from "@/utils/utils";
import type { JsxElement } from "@/utils/jsx/jsxElementTypes";
import type { NavTreeNode } from "@/utils/types";
import { TempoElement } from "@/utils/tempoElement";
import {
  computeComponentFileKey,
  getInstanceUniqueKey,
  useInstancesStore,
  type Instance,
} from "./instancesStore";
import {
  findMatchingInstanceCodebaseIds,
  extractInstanceFromNavNode,
  flattenStoryboardsFromPages,
} from "./instanceUtils";
import { loadComponentInstancesFromSupabase } from "./instancesSupabaseSync";
import { STORYBOARD_TYPES } from "@/utils/constants";
import type { SelectedInstanceInfo } from "./instancesStore";

// ═══════════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Summary of instances in a storyboard for the grouped view.
 * One group per storyboard that contains instances of the selected component.
 */
export interface StoryboardGroup {
  storyboardId: string;
  storyboardName: string;
  pageId: string;
  count: number; // Total instances in this storyboard
  firstInstance: Instance; // For preview screenshot
}

/**
 * Batch collections for registering instance metadata.
 */
interface MetadataBatch {
  navNodes: Record<string, NavTreeNode>;
  elementKeyToInstanceInfo: Record<string, SelectedInstanceInfo>;
  initialHotReloadTs: Record<string, number>;
}

/**
 * Registers an instance's metadata (navNode, element key lookup, initial hotReloadTs).
 * Can be called with a batch to accumulate updates, or null to apply immediately.
 */
function registerInstanceMetadata(
  instance: Instance,
  instanceKey: string,
  navNode: NavTreeNode,
  batch: MetadataBatch | null,
): void {
  const store = useInstancesStore.getState();
  const elementKey = TempoElement.makeKey(
    instance.instanceCodebaseId,
    instance.storyboardId,
    instance.uniquePath,
  );
  const info: SelectedInstanceInfo = {
    instanceKey,
    storyboardId: instance.storyboardId,
    pageId: instance.pageId,
  };

  if (batch) {
    batch.elementKeyToInstanceInfo[elementKey] = info;
    batch.navNodes[instanceKey] = navNode;
    if (instance.hotReloadTs && !store.getInitialHotReloadTs(instanceKey)) {
      batch.initialHotReloadTs[instanceKey] = instance.hotReloadTs;
    }
  } else {
    store.setElementKeyToInstanceInfo(elementKey, info);
    store.setNavNode(instanceKey, navNode);
    if (instance.hotReloadTs && !store.getInitialHotReloadTs(instanceKey)) {
      store.setInitialHotReloadTs(instanceKey, instance.hotReloadTs);
    }
  }
}

/**
 * Applies batched metadata updates to the store.
 */
function applyMetadataBatch(batch: MetadataBatch): void {
  const store = useInstancesStore.getState();
  if (Object.keys(batch.elementKeyToInstanceInfo).length > 0) {
    store.setBatchElementKeyToInstanceInfo(batch.elementKeyToInstanceInfo);
  }
  if (Object.keys(batch.navNodes).length > 0) {
    store.setBatchNavNodes(batch.navNodes);
  }
  if (Object.keys(batch.initialHotReloadTs).length > 0) {
    store.setBatchInitialHotReloadTs(batch.initialHotReloadTs);
  }
}

/**
 * Storyboard groups organized by page (for grouped view).
 */
export interface PageStoryboardGroups {
  pageId: string;
  pageName: string;
  storyboardGroups: StoryboardGroup[];
}

/**
 * Result of useInstancesData.
 */
export interface UseInstancesDataResult {
  /** Grouped view - storyboard groups for current page */
  currentPageStoryboardGroups: StoryboardGroup[];

  /** Grouped view - storyboard groups organized by other pages */
  otherPagesGroups: PageStoryboardGroups[];

  /** Drilled view - instances in the selected storyboard, sorted by DOM order */
  drilledInstances: Instance[];

  /** Info about selected storyboard for breadcrumb display */
  selectedStoryboardInfo: { pageName: string; storyboardName: string } | null;

  /** All instances that should be captured (based on current view) */
  instancesToCapture: Instance[];

  /** Component file key for store lookups */
  componentFileKey: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN HOOK
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Combined hook for syncing and reading instance data.
 *
 * @param component - The selected component definition, or null if none selected
 * @param selectedStoryboardId - The drilled storyboard's ID, or null for grouped view
 * @returns Instance data organized by page for rendering and capture
 */
export function useInstancesData(
  component: JsxElement | null,
  selectedStoryboardId: string | null,
): UseInstancesDataResult {
  const canvasId = useCanvasId();

  // Compute component file key for store lookups
  const componentFileKey = useMemo(
    () =>
      canvasId && component
        ? computeComponentFileKey(
            component.filename || "",
            component.componentName || "",
            canvasId,
          )
        : "",
    [canvasId, component?.filename, component?.componentName],
  );

  // Get stores for sync
  const treeElementLookup = useCodebaseStore(
    (state) => state.treeElementLookup,
  );
  const pages = usePagesStore((state) =>
    canvasId ? state.pages[canvasId] : undefined,
  );
  const selectedPageId = usePagesStore((state) =>
    canvasId ? state.selectedPageId[canvasId] : undefined,
  );

  // Subscribe to NavTree lookup for sync triggering
  const sbIdToCodebaseIdToNavNodeLookup = useCanvasStore(
    (state) => state.sbIdToCodebaseIdToNavNodeLookup,
  );

  // Derive storyboards from pages (with pageId hydrated)
  const allStoryboards = useMemo(
    () => (pages ? flattenStoryboardsFromPages(pages) : []),
    [pages],
  );

  // Find matching instance codebaseIds for the component
  const matchingInstanceCodebaseIds = useMemo(
    () =>
      component
        ? findMatchingInstanceCodebaseIds(component, treeElementLookup)
        : [],
    [component, treeElementLookup],
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // SYNC EFFECT: NavTree → instancesStore
  // ═══════════════════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!component || !canvasId || !matchingInstanceCodebaseIds.length) return;
    if (allStoryboards.length === 0) return;

    const store = useInstancesStore.getState();
    const validStoryboardIds = new Set(allStoryboards.map((s) => s.id));

    // Clean up instances from deleted storyboards
    store.clearInvalidStoryboards(
      canvasId,
      componentFileKey,
      validStoryboardIds,
    );

    // Track storyboards we've already synced (grouped view only)
    const syncedStoryboards = new Set<string>();

    for (const storyboard of allStoryboards) {
      const navLookup = sbIdToCodebaseIdToNavNodeLookup[storyboard.id];
      if (!navLookup) continue;

      const existingInstances =
        store.instancesByCanvas[canvasId]?.[componentFileKey]?.[
          storyboard.id
        ] ?? {};
      const newInstances: Instance[] = [];

      if (selectedStoryboardId === null) {
        // GROUPED VIEW: Sync one representative instance per storyboard

        // Skip if already synced in this effect run
        if (syncedStoryboards.has(storyboard.id)) {
          continue;
        }

        // Check if we already have a NavTree-confirmed instance (has navNode registered)
        const hasNavTreeInstance = Object.keys(existingInstances).some(
          (key) => store.getNavNode(key) !== undefined,
        );
        if (hasNavTreeInstance) {
          syncedStoryboards.add(storyboard.id);
          continue;
        }

        // Find the first matching instance in this storyboard
        for (const codebaseId of matchingInstanceCodebaseIds) {
          const navNodes = navLookup[codebaseId];
          if (!navNodes?.length) continue;

          const instanceFilepath =
            treeElementLookup[codebaseId]?.filename || "";
          const instance = extractInstanceFromNavNode(
            navNodes[0],
            codebaseId,
            storyboard.id,
            storyboard.pageId,
            component,
            instanceFilepath,
          );

          if (instance) {
            const instanceKey = getInstanceUniqueKey(instance, canvasId);
            newInstances.push(instance);
            syncedStoryboards.add(storyboard.id);
            registerInstanceMetadata(
              instance,
              instanceKey,
              navNodes[0],
              null, // Apply immediately (few updates expected in grouped view)
            );
            break; // Only need one instance per storyboard for grouped view
          }
        }
      } else if (selectedStoryboardId === storyboard.id) {
        // DRILLED VIEW: Sync ALL instances for the selected storyboard

        // Collect all navNodes for matching codebaseIds in this storyboard
        const allNavNodes: Array<{ codebaseId: string; navNode: NavTreeNode }> =
          [];
        for (const codebaseId of matchingInstanceCodebaseIds) {
          const navNodes = navLookup[codebaseId];
          if (navNodes?.length) {
            for (const navNode of navNodes) {
              allNavNodes.push({ codebaseId, navNode });
            }
          }
        }

        if (allNavNodes.length === 0) continue;

        // Check if we already have all instances with their navNodes
        const existingKeys = new Set(
          Object.values(existingInstances).map((i) =>
            getInstanceUniqueKey(i, canvasId),
          ),
        );
        const allExistWithNavNodes =
          existingKeys.size === allNavNodes.length &&
          [...existingKeys].every((key) => store.getNavNode(key) !== undefined);

        if (allExistWithNavNodes) continue;

        // Batch metadata updates (many updates expected in drilled view)
        const batch: MetadataBatch = {
          navNodes: {},
          elementKeyToInstanceInfo: {},
          initialHotReloadTs: {},
        };

        for (const { codebaseId, navNode } of allNavNodes) {
          const instanceFilepath =
            treeElementLookup[codebaseId]?.filename || "";
          const instance = extractInstanceFromNavNode(
            navNode,
            codebaseId,
            storyboard.id,
            storyboard.pageId,
            component,
            instanceFilepath,
          );

          if (instance) {
            const instanceKey = getInstanceUniqueKey(instance, canvasId);
            newInstances.push(instance);
            registerInstanceMetadata(instance, instanceKey, navNode, batch);
          }
        }

        applyMetadataBatch(batch);
      }

      // Update store if we have new instances
      if (newInstances.length > 0) {
        // Merge with existing instances (preserve instances not being replaced)
        const updatedInstanceKeys = new Set(
          newInstances.map((i) => getInstanceUniqueKey(i, canvasId)),
        );
        const preserved = Object.values(existingInstances).filter(
          (i) => !updatedInstanceKeys.has(getInstanceUniqueKey(i, canvasId)),
        );

        store.setInstancesForStoryboard(
          canvasId,
          componentFileKey,
          storyboard.id,
          [...preserved, ...newInstances],
        );
      }
    }
  }, [
    canvasId,
    componentFileKey,
    selectedStoryboardId,
    allStoryboards,
    matchingInstanceCodebaseIds,
    sbIdToCodebaseIdToNavNodeLookup,
    treeElementLookup,
    component,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // LOAD EFFECT: Supabase → instancesStore (component-level)
  // ═══════════════════════════════════════════════════════════════════════════

  // Load cached instances from Supabase when the selected component changes.
  // This runs in parallel with NavTree sync and provides screenshot data.
  // Supabase data is treated as "stale" - it only fills in contentWindowData/dragData.
  useEffect(() => {
    if (!canvasId || !componentFileKey) return;

    let cancelled = false;

    const loadFromSupabase = async () => {
      const supabaseData = await loadComponentInstancesFromSupabase(
        canvasId,
        componentFileKey,
      );

      if (cancelled || Object.keys(supabaseData).length === 0) return;

      // Merge Supabase data into store (Supabase is stale, NavTree is fresh)
      useInstancesStore
        .getState()
        .mergeSupabaseInstances(canvasId, componentFileKey, supabaseData);
    };

    loadFromSupabase();

    return () => {
      cancelled = true;
    };
  }, [canvasId, componentFileKey]);

  // ═══════════════════════════════════════════════════════════════════════════
  // READ: Get instances from store
  // ═══════════════════════════════════════════════════════════════════════════

  const componentStoreData = useInstancesStore((state) => {
    if (!canvasId || !componentFileKey) return null;
    return state.instancesByCanvas[canvasId]?.[componentFileKey] ?? null;
  });

  // Flatten all instances from store
  const storeInstances = useMemo(() => {
    if (!componentStoreData) return [];

    const result: Instance[] = [];
    for (const storyboardData of Object.values(componentStoreData)) {
      for (const instance of Object.values(storyboardData)) {
        result.push(instance);
      }
    }
    return result;
  }, [componentStoreData]);

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTE: Per-storyboard counts from NavTree
  // ═══════════════════════════════════════════════════════════════════════════

  // Map: storyboardId -> count of matching instances
  const instanceCountsByStoryboard = useMemo(() => {
    const counts = new Map<string, number>();

    for (const storyboard of allStoryboards) {
      let count = 0;
      for (const codebaseId of matchingInstanceCodebaseIds) {
        const navNodes =
          sbIdToCodebaseIdToNavNodeLookup[storyboard.id]?.[codebaseId];
        if (navNodes?.length) {
          count += navNodes.length;
        }
      }
      if (count > 0) {
        counts.set(storyboard.id, count);
      }
    }

    return counts;
  }, [
    sbIdToCodebaseIdToNavNodeLookup,
    allStoryboards,
    matchingInstanceCodebaseIds,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // HELPERS: Page and storyboard name lookups
  // ═══════════════════════════════════════════════════════════════════════════

  // Build pageId -> pageName lookup
  const pageNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    if (pages) {
      for (const page of pages) {
        lookup[page.id] = page.name || "Untitled";
      }
    }
    return lookup;
  }, [pages]);

  // Build storyboardId -> storyboardName lookup
  // For application storyboards, show the route; for others, use name or storyComponentName
  const storyboardNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const storyboard of allStoryboards) {
      if (storyboard.type === STORYBOARD_TYPES.APPLICATION) {
        lookup[storyboard.id] = storyboard.route || "/";
      } else {
        lookup[storyboard.id] =
          storyboard.name || storyboard.storyComponentName || "Untitled";
      }
    }
    return lookup;
  }, [allStoryboards]);

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTE: Storyboard groups for grouped view
  // ═══════════════════════════════════════════════════════════════════════════

  const { currentPageStoryboardGroups, otherPagesGroups } = useMemo(() => {
    if (selectedStoryboardId !== null) {
      return { currentPageStoryboardGroups: [], otherPagesGroups: [] };
    }

    // Build storyboard groups from store instances
    // Map: storyboardId -> { firstInstance, count, pageId }
    const storyboardMap = new Map<
      string,
      { firstInstance: Instance; storeCount: number; pageId: string }
    >();

    for (const instance of storeInstances) {
      if (!storyboardMap.has(instance.storyboardId)) {
        storyboardMap.set(instance.storyboardId, {
          firstInstance: instance,
          storeCount: 1,
          pageId: instance.pageId,
        });
      } else {
        const existing = storyboardMap.get(instance.storyboardId)!;
        existing.storeCount++;
      }
    }

    // Build StoryboardGroup array with accurate counts
    const groups: StoryboardGroup[] = [];
    for (const [storyboardId, data] of storyboardMap) {
      // Use max of NavTree count and store count for accuracy
      const navTreeCount = instanceCountsByStoryboard.get(storyboardId) ?? 0;
      const count = Math.max(navTreeCount, data.storeCount);

      groups.push({
        storyboardId,
        storyboardName: storyboardNameLookup[storyboardId] || "Untitled",
        pageId: data.pageId,
        count,
        firstInstance: data.firstInstance,
      });
    }

    // Split by page - current page groups vs other pages groups
    const currentGroups: StoryboardGroup[] = [];
    const otherByPage = new Map<string, StoryboardGroup[]>();

    for (const group of groups) {
      if (group.pageId === selectedPageId) {
        currentGroups.push(group);
      } else {
        const existing = otherByPage.get(group.pageId) || [];
        existing.push(group);
        otherByPage.set(group.pageId, existing);
      }
    }

    // Convert other pages map to array of PageStoryboardGroups
    const otherPagesArray: PageStoryboardGroups[] = [];
    for (const [pageId, storyboardGroups] of otherByPage) {
      otherPagesArray.push({
        pageId,
        pageName: pageNameLookup[pageId] || "Untitled",
        storyboardGroups,
      });
    }

    return {
      currentPageStoryboardGroups: currentGroups,
      otherPagesGroups: otherPagesArray,
    };
  }, [
    selectedStoryboardId,
    storeInstances,
    instanceCountsByStoryboard,
    storyboardNameLookup,
    selectedPageId,
    pageNameLookup,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTE: Drilled view - instances in selected storyboard
  // ═══════════════════════════════════════════════════════════════════════════

  const drilledInstances = useMemo(() => {
    if (selectedStoryboardId === null) return [];

    // Filter instances by selected storyboard
    const instances = storeInstances.filter(
      (i) => i.storyboardId === selectedStoryboardId,
    );

    // Sort by DOM order (domUniquePath provides natural reading order)
    instances.sort((a, b) => a.domUniquePath.localeCompare(b.domUniquePath));

    return instances;
  }, [selectedStoryboardId, storeInstances]);

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTE: Selected storyboard info for breadcrumb
  // ═══════════════════════════════════════════════════════════════════════════

  const selectedStoryboardInfo = useMemo(() => {
    if (selectedStoryboardId === null) return null;

    // Find the storyboard to get its page
    const storyboard = allStoryboards.find(
      (s) => s.id === selectedStoryboardId,
    );
    if (!storyboard) return null;

    return {
      pageName: pageNameLookup[storyboard.pageId] || "Untitled",
      storyboardName: storyboardNameLookup[selectedStoryboardId] || "Untitled",
    };
  }, [
    selectedStoryboardId,
    allStoryboards,
    pageNameLookup,
    storyboardNameLookup,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPUTE: Instances to capture
  // ═══════════════════════════════════════════════════════════════════════════

  const instancesToCapture = useMemo(() => {
    if (selectedStoryboardId !== null) {
      // Drilled view: capture all instances in selected storyboard
      return drilledInstances;
    }
    // Grouped view: capture first instance from each storyboard group
    const instances: Instance[] = [];
    for (const group of currentPageStoryboardGroups) {
      instances.push(group.firstInstance);
    }
    for (const pageGroup of otherPagesGroups) {
      for (const group of pageGroup.storyboardGroups) {
        instances.push(group.firstInstance);
      }
    }
    return instances;
  }, [
    selectedStoryboardId,
    drilledInstances,
    currentPageStoryboardGroups,
    otherPagesGroups,
  ]);

  return {
    currentPageStoryboardGroups,
    otherPagesGroups,
    drilledInstances,
    selectedStoryboardInfo,
    instancesToCapture,
    componentFileKey,
  };
}

/**
 * Hook to get the current page name.
 */
export function useCurrentPageName(): string {
  const canvasId = useCanvasId();
  return usePagesStore((state) => {
    if (!canvasId) return "This Page";
    const selectedPageId = state.selectedPageId[canvasId];
    const pages = state.pages[canvasId];
    return pages?.find((p) => p.id === selectedPageId)?.name || "This Page";
  });
}
